// src/screens/CreateLeagueScreen.js
// Post a League — the ANNOUNCEMENT plus the competition's SHAPE. A league
// posting is what recruits players: game, format, team size, platform, a
// roster cap that matches the shape, the CALENDAR (the exact day and time of
// every round/race — founder 2026-08-18), what it costs, how it pays, a name
// and a description. The shape (format + team size + roster cap) is set at
// posting time (founder 2026-07-20) so Command Center edits can't collide
// with what joiners were promised; the remaining dials (host mode, tracks,
// car class) get sensible defaults and are tuned afterward in the League
// Command Center (commissioner-only, editable until the league locks).
//
// Layout (founder 2026-08-18): five numbered cards — Game (searchable pill
// picker), The posting, Field & entry, Schedule (pattern generator + a
// per-round date/time row for every round), Post. The platform season end
// is informational only: leagues may run any number of days past it.
// Server: functions/modules/leagues.js createLeague (moderates name +
// description, re-enforces every limit incl. the calendar rules).
import React, { useEffect, useMemo, useState } from 'react';
import {
  View, Text, SafeAreaView, ScrollView, StyleSheet,
  TouchableOpacity, TextInput, ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../../firebaseConfig';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useError } from '../components/ErrorContext';
import UpgradeMembershipModal from '../components/UpgradeMembershipModal';
import BuyCritsModal from '../components/BuyCritsModal';
import { GAME_PROFILES, BR_PLATFORM_MAX_PLAYERS } from '../constants/gameData';
// Open catalog: browse-catalog games + free-text "Other" run SOLO H2H
// leagues on the generic community profile (player-reported results).
import {
  EXTRA_EVENT_GAMES,
  COMMUNITY_GENRES,
  VISION_RESOLVE_GAMES,
  buildCommunityGameProfile,
  communityGameKeyFromName,
  gameUsesManualResults,
  isKnownCommunityGame,
  normalizeCommunityGameName,
} from '../constants/communityGames';
import { COMMUNITY_GAME_CATALOG } from '../constants/communityCatalog';
import { useGameAvailability } from '../hooks/useGameAvailability';
import useCritsEnabled from '../hooks/useCritsEnabled';
import { fmtCrits } from '../utils/currencyHelper';
import LeagueCalendarEditor from '../components/LeagueCalendarEditor';
import { RACES_MIN, RACES_MAX, DEFAULT_PATTERN, LEAGUE_ADMIN_MIN_LEAD_MS, resizeRoundTimes } from '../utils/leagueSchedule';

const GOLD = '#FCD000';
const CYAN = '#4FC3F7';

// ── Mirrors functions/constants/leagueEconomy.js (server-enforced) ──────────
const MIN_PLAYERS = 4;
const MAX_PLAYERS_SOLO = 32;   // solo H2H round-robin
const MAX_TEAMS = 16;          // team leagues (roster entries are TEAMS)
const MAX_PLAYERS_RACE = 16;   // bound by the game lobby
const MIN_ENTRY = 5;
const MAX_DESCRIPTION = 500;
// Creation fee ladder by roster size — the creator's buy-in. Playing
// creators pay max(entry, fee) (the fee IS their entry); team commissioners
// pay the fee only. Everything seeds the Crit pool.
const creationFee = (n) => (n <= 8 ? 10 : n <= 16 ? 20 : 30);
// Roster pill values (a 4..32 wall of pills is unusable — common sizes only)
const SOLO_SIZES = [4, 5, 6, 7, 8, 10, 12, 14, 16, 20, 24, 28, 32];
// Warzone BR race leagues: ladder of common roster sizes. The pill list is
// windowed per format below to fmt.minPlayers .. effective cap (solo lobby
// cap clamped to BR_PLATFORM_MAX_PLAYERS) - mirrors LeagueCommandCenterScreen.
const BR_ROSTER_SIZES = [4, 5, 6, 7, 8, 10, 12, 14, 16, 20, 24, 28, 32, 36, 40, 44, 48, 50];
const MEETINGS_OPTIONS = [1, 2, 3, 4];
const baseRoundsFor = (n) => (n % 2 === 0 ? n - 1 : n);
const TIERED_TABLES = {
  3: [50, 30, 20],
  4: [45, 27, 16, 12],
  5: [40, 25, 15, 10, 10],
  6: [38, 24, 14, 9, 8, 7],
  7: [36, 22, 13, 9, 8, 6, 6],
  8: [35, 21, 12, 9, 7, 6, 5, 5],
  9: [34, 20, 12, 8, 7, 6, 5, 4, 4],
};
const payoutSplits = (mode, n) => {
  if (mode === 'winner') return [100];
  if (mode === 'podium') return [60, 30, 10];
  const paid = Math.min(9, Math.max(3, Math.floor(n / 2) + 1));
  return TIERED_TABLES[paid];
};
// Quick picks shown before the creator types — the flagship full-profile
// games. Everything else (the whole open catalog) is one search away.
const FEATURED_GAMES = ['gt7', 'mdn', 'cod', 'mvr', 'sf6', 'val', 'rkl', 'n2k'];
const MAX_SEARCH_RESULTS = 14;

// Default first round: tomorrow at the next full hour.
const defaultFirstRound = () => {
  const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
  d.setMinutes(0, 0, 0);
  return d.getTime();
};

function Pill({ label, active, onPress, disabled, small, icon }) {
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      style={[st.pill, small && st.pillSmall, active && st.pillActive, disabled && st.pillDisabled]}
    >
      {icon ? <Ionicons name={icon} size={12} color={active ? '#000' : '#bbb'} style={{ marginRight: 5 }} /> : null}
      <Text style={[st.pillText, small && st.pillTextSmall, active && st.pillTextActive]}>{label}</Text>
    </TouchableOpacity>
  );
}

function Section({ n, title, hint, children }) {
  return (
    <View style={st.card}>
      <View style={st.cardHead}>
        <View style={st.cardNum}><Text style={st.cardNumText}>{n}</Text></View>
        <View style={{ flex: 1 }}>
          <Text style={st.cardTitle}>{title}</Text>
          {!!hint && <Text style={st.cardHint}>{hint}</Text>}
        </View>
      </View>
      {children}
    </View>
  );
}

// ── Game search index: full profiles, browse-catalog extras, community
// catalog (names + aliases). One list, one search box.
function buildGameIndex(gameAvailability) {
  const out = [];
  for (const key of Object.keys(GAME_PROFILES)) {
    if (gameAvailability[key]?.enabled === false) continue;
    const gp = GAME_PROFILES[key];
    if (Object.keys(gp?.formats || {}).length === 0) continue;
    out.push({
      id: `p:${key}`, tier: 'profile', key, name: gp.displayName || key,
      terms: [gp.displayName, gp.abbr, key].filter(Boolean).map((s) => String(s).toLowerCase()),
      verified: VISION_RESOLVE_GAMES.has(key),
    });
  }
  for (const key of Object.keys(EXTRA_EVENT_GAMES)) {
    const g = EXTRA_EVENT_GAMES[key];
    out.push({
      id: `x:${key}`, tier: 'catalog', key, name: g.displayName || key,
      terms: [g.displayName, g.abbr, key].filter(Boolean).map((s) => String(s).toLowerCase()),
    });
  }
  const seenExtras = new Set(Object.keys(EXTRA_EVENT_GAMES));
  for (const entry of COMMUNITY_GAME_CATALOG) {
    if (entry.key && seenExtras.has(entry.key)) {
      // Redirect entry: fold its aliases into the extra's terms.
      const x = out.find((o) => o.id === `x:${entry.key}`);
      if (x) x.terms.push(...(entry.aliases || []).map((a) => String(a).toLowerCase()));
      continue;
    }
    out.push({
      id: `c:${entry.name}`, tier: 'community', key: null, name: entry.name, genre: entry.genre || null,
      terms: [entry.name, ...(entry.aliases || [])].map((s) => String(s).toLowerCase()),
    });
  }
  return out;
}

function searchGames(index, query) {
  const q = String(query || '').toLowerCase().trim();
  if (!q) return [];
  const tierRank = { profile: 0, catalog: 1, community: 2 };
  const scored = [];
  for (const g of index) {
    let best = Infinity;
    for (const t of g.terms) {
      if (t === q) best = Math.min(best, 0);
      else if (t.startsWith(q)) best = Math.min(best, 1);
      else if (t.split(/\s+/).some((w) => w.startsWith(q))) best = Math.min(best, 2);
      else if (t.includes(q)) best = Math.min(best, 3);
    }
    if (best < Infinity) scored.push({ g, score: best * 10 + tierRank[g.tier] });
  }
  scored.sort((a, b) => a.score - b.score || a.g.name.localeCompare(b.g.name));
  return scored.slice(0, MAX_SEARCH_RESULTS).map((s) => s.g);
}

export default function CreateLeagueScreen({ navigation }) {
  const { showError } = useError();
  const { games: gameAvailability } = useGameAvailability();
  // Evergreen step 2.3: settings/economy.critsEnabled hides the Crit economy
  // (entry, creation fee, pool) — derived below, never seeded into state.
  const critsOn = useCritsEnabled();
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const u = auth.currentUser;
    if (!u) return;
    const unsub = onSnapshot(doc(db, 'users', u.uid), (snap) => {
      setUserData(snap.exists() ? snap.data() : null);
    });
    return () => unsub();
  }, []);

  // ── Game (searchable) ────────────────────────────────────────────────────
  const gameIndex = useMemo(() => buildGameIndex(gameAvailability), [gameAvailability]);
  const [gameQuery, setGameQuery] = useState('');
  const searchResults = useMemo(() => searchGames(gameIndex, gameQuery), [gameIndex, gameQuery]);
  const featured = useMemo(() => FEATURED_GAMES
    .map((k) => gameIndex.find((g) => g.id === `p:${k}`)).filter(Boolean), [gameIndex]);

  const [game, setGame] = useState(null);
  const [customGameName, setCustomGameName] = useState('');
  const [otherGenre, setOtherGenre] = useState('other');
  const communityName = normalizeCommunityGameName(customGameName);
  // A pick outside GAME_PROFILES rides the generic community profile.
  const isOpenPick = !!game && !GAME_PROFILES[game];
  const gp = game
    ? (GAME_PROFILES[game] || buildCommunityGameProfile(
      game === 'other' ? (communityGameKeyFromName(communityName) || 'other') : game,
      game === 'other' ? (communityName || null) : null,
      game === 'other' ? otherGenre : undefined,
    ))
    : null;
  const [platform, setPlatform] = useState(null);

  const pickGame = (g) => {
    setGameQuery('');
    if (g.tier === 'community') {
      // Catalog game → the "Other" path with a canonical name (the backend
      // slugs it to the shared cg_* key; genre comes from the catalog).
      setGame('other');
      setCustomGameName(g.name);
      return;
    }
    setGame(g.key);
    setCustomGameName('');
  };
  const pickFreeText = () => {
    setGame('other');
    setCustomGameName(normalizeCommunityGameName(gameQuery));
    setGameQuery('');
  };
  const clearGame = () => { setGame(null); setCustomGameName(''); setGameQuery(''); };
  const selectedGameLabel = !game ? null
    : game === 'other'
      ? (gp?.displayName && isKnownCommunityGame(communityGameKeyFromName(communityName)) ? gp.displayName : (communityName || 'Other game'))
      : ((GAME_PROFILES[game] || EXTRA_EVENT_GAMES[game])?.displayName || game);

  // Posting-time SHAPE (founder 2026-07-20): format + team size lock into
  // the posting so Command Center edits can't collide with what joiners were
  // promised. formatName null = the game's first format (server default) -
  // racing games keep their fixed racing flow and Madden has no format
  // picker (mirrors Create Events).
  const [formatName, setFormatName] = useState(null);
  const [teamSize, setTeamSize] = useState(1);

  // Season end (public getSeason callable) — informational: leagues may run
  // past it (founder 2026-08-18); members get notified before a season roll.
  const [seasonEnd, setSeasonEnd] = useState(null);
  useEffect(() => {
    httpsCallable(getFunctions(undefined, 'us-central1'), 'getSeason')({})
      .then(({ data }) => setSeasonEnd(data?.endsAt || null))
      .catch(() => setSeasonEnd(null));
  }, []);

  useEffect(() => {
    const ap = gp?.allowedPlatforms || [];
    setPlatform(ap.find((p) => String(p).toLowerCase() === 'cross-platform') || ap[0] || null);
  }, [game]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the platform ON the resolved game's list. Typing an "Other" name
  // can swap the profile without changing `game` (the community catalog
  // resolves "mario kart" to the Switch-only Mario Kart World), and the
  // server rejects an off-list platform.
  // (keyed on a joined string — gp is rebuilt each render, so the array
  // identity alone would re-run this every pass.)
  const allowedPlatformsKey = (gp?.allowedPlatforms || []).join('|');
  useEffect(() => {
    const ap = gp?.allowedPlatforms || [];
    if (ap.length === 0 || ap.includes(platform)) return;
    setPlatform(ap.find((p) => String(p).toLowerCase() === 'cross-platform') || ap[0]);
  }, [allowedPlatformsKey, platform]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset the shape when the game changes. Pickable games start on their
  // first format (CDL Hardpoint for COD - order matters in gameData); the
  // rest post with formatName null and the server defaults to the first
  // format. Team size prefers Solo when the format sanctions it (same rule
  // as validateLeagueSettings).
  useEffect(() => {
    const prof = game ? (GAME_PROFILES[game] || buildCommunityGameProfile(game)) : null;
    const names = Object.keys(prof?.formats || {}).filter((n) => {
      const f = prof.formats[n];
      if (f?.battleRoyale !== true) return true;
      return (f.teamSizes || []).includes(1);
    });
    const pickable = !!prof && prof.type !== 'racing' && game !== 'mdn' && names.length > 1;
    const first = pickable ? names[0] : null;
    setFormatName(first);
    const f = first ? prof.formats[first] : Object.values(prof?.formats || {})[0];
    const sizes = Array.isArray(f?.teamSizes) && f.teamSizes.length ? f.teamSizes : [1];
    setTeamSize(f?.battleRoyale ? 1
      : (sizes.includes(1) ? 1 : (f?.defaultTeamSize || sizes[0])));
  }, [game]); // eslint-disable-line react-hooks/exhaustive-deps

  const [maxPlayers, setMaxPlayers] = useState(8);
  const [entryCost, setEntryCost] = useState(String(MIN_ENTRY));
  const [payoutMode, setPayoutMode] = useState('podium');
  const [leagueName, setLeagueName] = useState('');
  const [description, setDescription] = useState('');
  const [meetings, setMeetings] = useState(1);
  const [racesCount, setRacesCount] = useState(8);
  const [racesInput, setRacesInput] = useState('8');
  const [creating, setCreating] = useState(false);

  // ── Calendar (founder 2026-08-18): the exact day + time of every round ──
  // A pattern generates the rows; every row stays editable. Row 1 IS the
  // league start.
  const [pattern, setPattern] = useState(DEFAULT_PATTERN);
  const [roundTimes, setRoundTimes] = useState(() => [defaultFirstRound()]);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [critShortfall, setCritShortfall] = useState(null);

  const entryNum = critsOn ? Math.max(0, Math.floor(Number(entryCost) || 0)) : 0;
  const entryBelowMin = critsOn && entryNum < MIN_ENTRY;
  const isRacingGame = gp?.type === 'racing';

  // Postable formats: everything except BR playlists whose squad sizes lack
  // Solos (Ranked Play trios) - league BR is solos-only v1, so those can
  // never validate. Same filter as LeagueCommandCenterScreen's formatNames.
  const formatList = useMemo(() => Object.keys(gp?.formats || {}).filter((n) => {
    const f = gp?.formats?.[n];
    if (f?.battleRoyale !== true) return true;
    return (f.teamSizes || []).includes(1);
  }), [gp]);
  const showFormatPicker = !!gp && !isRacingGame && game !== 'mdn' && formatList.length > 1;
  // Selected format (null = the game's first format, the server default).
  const selFmt = (formatName && gp?.formats?.[formatName]) || Object.values(gp?.formats || {})[0] || null;
  const isBRFormat = !!selFmt?.battleRoyale;
  // BR playlists run race-style full-lobby rounds (placement points) even
  // though COD isn't a racing game - they ride the race machinery.
  const isRaceLeague = isRacingGame || isBRFormat;
  // Community-game leagues are SOLO in v1 (server enforces the same).
  const fmtSizes = isOpenPick ? [1]
    : (Array.isArray(selFmt?.teamSizes) && selFmt.teamSizes.length ? selFmt.teamSizes : [1]);
  // BR team sizes are IN-GAME squad sizes, not league team sizes - hidden.
  const showTeamSizePicker = !!gp && !isRacingGame && !isBRFormat && fmtSizes.length > 1;
  const isTeamLeague = !isBRFormat && teamSize > 1;
  const roundNoun = isRaceLeague ? (isBRFormat ? 'match' : 'race') : 'round';

  // Roster cap matches the shape (mirrors the server's rosterCapFor). BR:
  // the CODCaster host spectates and never consumes a player slot, so the
  // roster runs to the playlist's SOLO lobby cap (maxPlayersBySquadSize[1]
  // when tuned, else maxPlayers), clamped to BR_PLATFORM_MAX_PLAYERS while
  // scoring is manual. BR rosters are also FLOORED at fmt.minPlayers - a
  // league below the game's start floor could never launch a round (the
  // server rejects it with league-roster-below-floor).
  const brFloor = Math.max(MIN_PLAYERS, Math.floor(Number(selFmt?.minPlayers)) || MIN_PLAYERS);
  const brRosterCap = Math.min(
    Number(selFmt?.maxPlayersBySquadSize?.[1]) || Number(selFmt?.maxPlayers) || 50,
    BR_PLATFORM_MAX_PLAYERS
  );
  const rosterCap = isBRFormat ? brRosterCap
    : isTeamLeague ? MAX_TEAMS
      : (isRacingGame ? MAX_PLAYERS_RACE : MAX_PLAYERS_SOLO);
  const rosterSizes = isBRFormat
    ? (brFloor >= rosterCap
      ? [rosterCap]
      : [brFloor, ...BR_ROSTER_SIZES.filter((n) => n > brFloor && n < rosterCap), rosterCap])
    : (isTeamLeague || isRacingGame)
      ? Array.from({ length: rosterCap - MIN_PLAYERS + 1 }, (_, i) => MIN_PLAYERS + i)
      : SOLO_SIZES;
  useEffect(() => {
    if (rosterSizes.includes(maxPlayers)) return;
    const clamped = Math.min(maxPlayers, rosterCap);
    setMaxPlayers(rosterSizes.includes(clamped) ? clamped
      : isBRFormat ? (rosterSizes[0] || rosterCap)
        : Math.min(8, rosterCap));
  }, [rosterCap, brFloor, isBRFormat]); // eslint-disable-line react-hooks/exhaustive-deps

  // Team leagues: the creator posts as commissioner (fee only, joins with
  // their squad later); solo (and BR - always solos): the fee folds into
  // their entry. Team entry is per PLAYER — captains reserve the spot, each
  // squad member pays a share.
  const fee = critsOn ? creationFee(maxPlayers) : 0;
  const creatorCost = isTeamLeague ? fee : Math.max(entryNum, fee);
  const splits = payoutSplits(payoutMode, maxPlayers);
  const potAtFull = creatorCost + entryNum * (isTeamLeague ? maxPlayers * teamSize : maxPlayers - 1);

  // ── Calendar sizing: one dated row per planned round ─────────────────────
  // h2h leagues schedule their WORST-CASE round count (full roster); fewer
  // joins at lock leave the tail unused. Race leagues schedule exactly
  // racesCount rounds.
  const totalRounds = isRaceLeague ? racesCount : meetings * baseRoundsFor(maxPlayers);
  const gamesPerEntrant = isRaceLeague ? racesCount : meetings * (maxPlayers - 1);
  const durationMin = selFmt?.durationMinutes || 30;
  useEffect(() => {
    setRoundTimes((prev) => (prev.length === totalRounds ? prev
      : resizeRoundTimes(prev, totalRounds, pattern, defaultFirstRound())));
  }, [totalRounds]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRaces = (n) => {
    const v = Math.min(RACES_MAX, Math.max(RACES_MIN, Math.floor(Number(n) || RACES_MIN)));
    setRacesCount(v);
    setRacesInput(String(v));
  };
  // The calendar editor validates its rows (order, spacing, lead time) and
  // reports back; the post button follows it.
  const [scheduleOk, setScheduleOk] = useState(false);

  const canCreate = !!game && !entryBelowMin && !creating && scheduleOk
    && leagueName.trim().length >= 3
    // "Other" needs a sluggable game name first.
    && (game !== 'other' || !!communityGameKeyFromName(communityName));

  const onCreate = async () => {
    if (!auth.currentUser) { navigation.navigate('Login'); return; }
    setCreating(true);
    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'createLeague');
      const { data } = await fn({
        game,
        // Open catalog: the backend slugs 'other' + gameName to a shared cg_* key.
        gameName: game === 'other' ? communityName : undefined,
        gameGenre: game === 'other' ? otherGenre : undefined,
        platform,
        maxPlayers,
        entryCostCrits: entryNum,
        payoutMode,
        // The calendar: every round's exact start. Round 1 is the league
        // start (startAt sent too for pre-calendar readers).
        roundTimesMs: roundTimes,
        startAt: roundTimes[0],
        name: leagueName.trim(),
        description: description.trim() || null,
        // The competition's SHAPE (format + team size) locks into the
        // posting (founder 2026-07-20) so joiners get exactly what was
        // advertised. formatName null = server defaults to the game's first
        // format (racing games, Madden). BR is always solos (teamSize 1) and
        // rides the race machinery (racesCount). Remaining dials (host mode,
        // tracks...) default server-side and are tuned in the Command Center.
        formatName: formatName || null,
        teamSize,
        meetingsPerOpponent: isRaceLeague ? null : meetings,
        racesCount: isRaceLeague ? racesCount : null,
      });
      navigation.navigate('LeagueCommandCenter', { leagueId: data.leagueId, justCreated: true });
    } catch (err) {
      const msg = String(err?.message || '');
      // Nothing was charged when Crits are off, so the reassurance is dropped.
      const notCharged = critsOn ? ' Your Crits were not charged.' : '';
      if (msg.includes('member feature')) {
        setShowUpgradeModal(true);
      } else if (msg.includes('insufficient-crits')) {
        const have = Number((msg.match(/have\s+(\d+)/i) || [])[1]);
        const need = Number((msg.match(/need\s+(\d+)/i) || [])[1]);
        setCritShortfall({ have: Number.isFinite(have) ? have : 0, need: Number.isFinite(need) ? need : creatorCost });
      } else if (msg.includes('league-name-taken:')) {
        showError('Name already in use', `${msg.split('league-name-taken:')[1] || 'A league with that name already exists.'}${notCharged}`);
      } else if (msg.includes('title-not-approved:')) {
        showError('League name not approved', `${msg.split('title-not-approved:')[1] || 'Pick a different name.'}${notCharged}`);
      } else if (msg.includes('description-not-approved:')) {
        showError('Description not approved', `${msg.split('description-not-approved:')[1] || 'Reword your description.'}${notCharged}`);
      } else if (msg.includes('league-schedule-conflict:')) {
        // Must precede the generic league-schedule-* branch below.
        showError('Schedule conflict',
          `${msg.split('league-schedule-conflict:')[1]?.trim() || 'This league\'s rounds overlap another league you\'re in.'}${notCharged}`);
      } else if (msg.includes('league-schedule-')) {
        showError('Check the schedule', `${msg.split(/league-schedule-[a-z]+:/)[1]?.trim() || 'One of the dates needs a look.'}${notCharged}`);
      } else if (msg.includes('league-start-too-soon:')) {
        showError('Starts too soon', `${msg.split('league-start-too-soon:')[1]?.trim() || 'Move the first round later.'}${notCharged}`);
      } else if (msg.includes('league-slots-full')) {
        // Out of concurrent-league slots — offer the permanent Crit unlock
        // (server recomputes the price), then retry the post.
        const detail = msg.split('league-slots-full:')[1]?.trim() || 'All your league slots are in use.';
        if (typeof window !== 'undefined' && window.confirm(`${detail}\n\nUnlock it now?`)) {
          try {
            await httpsCallable(getFunctions(undefined, 'us-central1'), 'purchaseLeagueSlot')({});
            setCreating(false);
            return onCreate(); // slot secured — retry the post
          } catch (e2) {
            const m2 = String(e2?.message || '');
            if (m2.includes('insufficient-crits')) {
              const have2 = Number((m2.match(/have\s+(\d+)/i) || [])[1]);
              const need2 = Number((m2.match(/need\s+(\d+)/i) || [])[1]);
              setCritShortfall({ have: Number.isFinite(have2) ? have2 : 0, need: Number.isFinite(need2) ? need2 : 0 });
            } else {
              showError('Could not unlock slot', m2.replace(/^[a-z-]+:\s*/i, '') || 'Please try again.');
            }
          }
        }
      } else {
        showError('Could not create league', msg.replace(/^[a-z-]+:\s*/i, '') || 'Please try again.');
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <SafeAreaView style={st.safe}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={st.container}>
        <Text style={st.title}>Post a League</Text>
        <Text style={st.subtitle}>
          Recruit your field. Pick the game, shape the competition, {critsOn ? '' : 'and '}set the calendar — every
          {' '}{roundNoun}'s day and time{critsOn ? ' — and price the entry' : ''}. Players join knowing exactly what
          they're signing up for. After you post, your League Command Center fine-tunes the rest
          {isRacingGame ? ' (tracks, vehicle class, host mode)' : ' (host mode and more)'} until the league locks.
        </Text>

        {/* ══ 1 · GAME ══════════════════════════════════════════════════════ */}
        <Section n="1" title="Game" hint="Search any game — full profiles and the open catalog.">
          {game ? (
            <View style={st.selectedRow}>
              <View style={st.selectedChip}>
                <Ionicons name="game-controller" size={14} color="#000" style={{ marginRight: 6 }} />
                <Text style={st.selectedChipText}>{selectedGameLabel}</Text>
                <TouchableOpacity onPress={clearGame} style={st.selectedChipX} accessibilityLabel="Change game">
                  <Ionicons name="close" size={14} color="#000" />
                </TouchableOpacity>
              </View>
              {game !== 'other' && !!GAME_PROFILES[game] && VISION_RESOLVE_GAMES.has(game) && (
                <Text style={st.verifiedTag}>Verified results</Text>
              )}
              {!!game && (game === 'other' || gameUsesManualResults(game)) && (
                <Text style={st.reportedTag}>Player-reported results</Text>
              )}
            </View>
          ) : null}

          <View style={st.searchBox}>
            <Ionicons name="search" size={16} color="#8a8a8a" style={{ marginRight: 8 }} />
            <TextInput
              style={st.searchInput}
              value={gameQuery}
              onChangeText={setGameQuery}
              placeholder={game ? 'Search to switch games…' : 'Search games — "gran turismo", "madden", "halo"…'}
              placeholderTextColor="#666"
              autoCorrect={false}
              autoCapitalize="none"
              maxLength={50}
            />
            {!!gameQuery && (
              <TouchableOpacity onPress={() => setGameQuery('')} accessibilityLabel="Clear search">
                <Ionicons name="close-circle" size={16} color="#8a8a8a" />
              </TouchableOpacity>
            )}
          </View>

          {gameQuery.trim() ? (
            <>
              <View style={st.pillRow}>
                {searchResults.map((g) => (
                  <Pill key={g.id} label={g.name}
                    icon={g.tier === 'profile' ? (g.verified ? 'shield-checkmark' : 'game-controller') : undefined}
                    active={g.tier === 'community' ? (game === 'other' && communityName === g.name) : game === g.key}
                    onPress={() => pickGame(g)} />
                ))}
                {!!normalizeCommunityGameName(gameQuery)
                  && !searchResults.some((g) => g.name.toLowerCase() === gameQuery.trim().toLowerCase()) && (
                  <Pill label={`Use "${normalizeCommunityGameName(gameQuery)}"`} icon="add" onPress={pickFreeText} />
                )}
              </View>
              {searchResults.length === 0 && (
                <Text style={st.note}>No catalog match — use the name as typed and pick its genre below.</Text>
              )}
            </>
          ) : !game ? (
            <>
              <Text style={st.miniLabel}>Quick picks</Text>
              <View style={st.pillRow}>
                {featured.map((g) => (
                  <Pill key={g.id} label={g.name} icon={g.verified ? 'shield-checkmark' : undefined}
                    active={game === g.key} onPress={() => pickGame(g)} />
                ))}
              </View>
              <Text style={st.note}>Don't see yours? Search above — every game is playable.</Text>
            </>
          ) : null}

          {game === 'other' && (
            <>
              <Text style={st.label}>Game name</Text>
              <TextInput
                style={st.input}
                value={customGameName}
                onChangeText={setCustomGameName}
                placeholder="What game is it?"
                placeholderTextColor="#666"
                maxLength={50}
              />
              {/* Known games (community catalog + browse extras) carry a
                  canonical name and genre — the picker only shows for games
                  the catalog doesn't know. */}
              {!!communityName && isKnownCommunityGame(communityGameKeyFromName(communityName)) ? (
                <Text style={st.note}>
                  Matched: {gp?.displayName} — name and category are set automatically.
                </Text>
              ) : (
                <>
                  <Text style={st.label}>Genre</Text>
                  <View style={st.pillRow}>
                    {COMMUNITY_GENRES.map((g) => (
                      <Pill key={g.key} label={g.label} active={otherGenre === g.key} small
                        onPress={() => setOtherGenre(g.key)} />
                    ))}
                  </View>
                </>
              )}
            </>
          )}

          {/* Player-reported results apply to EVERY non-vision game — full-
              profile games (Tekken, NBA 2K, ...) included, not just open
              picks. League rounds are stamped manualResults the same way
              (gameUsesManualResults, leagues.js). */}
          {!!game && (game === 'other' || gameUsesManualResults(game)) && (
            <Text style={st.note}>
              Results are reported by the players: when the
              {(isRacingGame || gp?.genre === 'racing') ? ' race' : ' match'} ends, everyone taps
              MATCH ENDED and reports — matching reports post instantly, and anything contested
              gets reviewed.
            </Text>
          )}
          {isRacingGame && (
            <Text style={st.note}>
              Racing leagues run as a full-grid championship — every round is one race with the
              whole roster, scored on a points system (25-18-15-12-10-8-6-4-2-1).
            </Text>
          )}

          {/* Format — the first half of the posting's SHAPE. Racing games keep
              their fixed racing flow; Madden has no format picker (mirrors
              Create Events). */}
          {showFormatPicker && (
            <>
              <Text style={st.label}>Format</Text>
              <View style={st.pillRow}>
                {formatList.map((n) => (
                  <Pill key={n} label={n} active={formatName === n}
                    onPress={() => {
                      setFormatName(n);
                      const f = gp?.formats?.[n];
                      if (f?.battleRoyale) {
                        // BR playlists are SOLOS only in v1 (their teamSizes
                        // are in-game squad sizes).
                        setTeamSize(1);
                        return;
                      }
                      // Keep teamSize sanctioned for the new format.
                      const sizes = Array.isArray(f?.teamSizes) && f.teamSizes.length ? f.teamSizes : [1];
                      setTeamSize((t) => (sizes.includes(t) ? t
                        : (sizes.includes(1) ? 1 : (f?.defaultTeamSize || sizes[0]))));
                    }} />
                ))}
              </View>
              {!!selFmt?.description && <Text style={st.note}>{selFmt.description}</Text>}
            </>
          )}

          {/* Team size — the second half of the shape (formats with multiple
              sanctioned sizes, e.g. CDL 6..2, Gunfight 2v2/Solo). Hidden for
              BR playlists - solos-only in v1. */}
          {showTeamSizePicker && (
            <>
              <Text style={st.label}>Team size</Text>
              <View style={st.pillRow}>
                {[...fmtSizes].sort((a, b) => b - a).map((n) => (
                  <Pill key={n} label={n === 1 ? 'Solo' : `${n}v${n}`} active={teamSize === n}
                    onPress={() => setTeamSize(n)} />
                ))}
              </View>
            </>
          )}

          {/* What the chosen shape means - team (captains), BR series, or solo
              round-robin. */}
          {!!game && !isRacingGame && (
            isBRFormat ? (
              <Text style={st.note}>
                Full-lobby battle royale series - solos, placement points. Every round is one
                private match with the whole roster; standings by total points.
              </Text>
            ) : isTeamLeague ? (
              <Text style={st.note}>
                Team league - captains enter with their squad ({teamSize}v{teamSize}, locked into
                this posting). Squads can carry alternates: at least {teamSize} members must be
                match-ready (IGN + stream key) to enter, and the captain picks who starts each
                round. You post as commissioner and can join with your own team after.
              </Text>
            ) : (
              <Text style={st.note}>
                Solo league - a round-robin where every player meets every other player
                head-to-head. Standings by win-loss.
              </Text>
            )
          )}

          {/* Platform */}
          {!!gp?.allowedPlatforms?.length && (
            <>
              <Text style={st.label}>Platform</Text>
              <View style={st.pillRow}>
                {gp.allowedPlatforms.map((p) => (
                  <Pill key={p} label={p} active={platform === p} onPress={() => setPlatform(p)} />
                ))}
              </View>
              <Text style={st.note}>Platform-locked leagues only admit players with an IGN on that platform.</Text>
            </>
          )}
        </Section>

        {/* ══ 2 · THE POSTING ═══════════════════════════════════════════════ */}
        <Section n="2" title="The posting" hint="What players see in the lobby.">
          <Text style={st.label}>League name</Text>
          <TextInput
            style={st.input}
            value={leagueName}
            onChangeText={setLeagueName}
            maxLength={80}
            placeholder="e.g. Friday Night Gridiron"
            placeholderTextColor="#666"
          />
          <Text style={st.note}>
            Names are reviewed — no brand names, full game names, or offensive language.
          </Text>

          <Text style={st.label}>Description</Text>
          <TextInput
            style={[st.input, st.multiline]}
            value={description}
            onChangeText={setDescription}
            maxLength={MAX_DESCRIPTION}
            multiline
            numberOfLines={4}
            placeholder="Pitch your league: skill level, vibe, rules, schedule. (Game names are fine here.)"
            placeholderTextColor="#666"
          />
          <Text style={st.note}>
            {description.length}/{MAX_DESCRIPTION} — reviewed for harmful language and brand/copyright issues.
          </Text>
        </Section>

        {/* ══ 3 · FIELD & ENTRY ═════════════════════════════════════════════ */}
        <Section n="3" title="Field & entry"
          hint={critsOn ? 'Roster size, what it costs to join, how the pool pays out.' : 'Roster size.'}>
          <Text style={st.label}>How many {isTeamLeague ? 'teams' : 'players'} can join</Text>
          <View style={st.pillRow}>
            {rosterSizes.map((n) => (
              <Pill key={n} label={String(n)} active={maxPlayers === n} onPress={() => setMaxPlayers(n)} />
            ))}
          </View>
          {isBRFormat && (
            <Text style={st.note}>
              Warzone leagues need at least {brFloor} players in the lobby to launch each match.
            </Text>
          )}

          {critsOn && (
            <>
              <Text style={st.label}>Entry cost (Crits per player{isTeamLeague ? ' — each squad member pays their own share' : ''})</Text>
              <TextInput
                style={[st.input, st.inputShort]}
                value={entryCost}
                onChangeText={setEntryCost}
                keyboardType="numeric"
                placeholder={`${MIN_ENTRY} minimum`}
                placeholderTextColor="#666"
              />
              {entryBelowMin && (
                <Text style={st.warn}>League entry must be at least {MIN_ENTRY} Crits.</Text>
              )}
            </>
          )}

          {/* Payout mode only splits the Crit pool; hidden while Crits are OFF
              (the default mode still posts - createLeague requires one). */}
          {critsOn && (
            <>
          <Text style={st.label}>Payout mode</Text>
          <View style={st.pillRow}>
            <Pill label="Winner takes all" active={payoutMode === 'winner'} onPress={() => setPayoutMode('winner')} />
            <Pill label="Podium" active={payoutMode === 'podium'} onPress={() => setPayoutMode('podium')} />
            <Pill label="Tiered" active={payoutMode === 'tiered'} onPress={() => setPayoutMode('tiered')} />
          </View>
          <View style={st.infoBox}>
            <Text style={st.infoTitle}>
              Splits at {maxPlayers} {isTeamLeague ? 'teams' : 'players'}{critsOn ? ` · pool ≈ ${fmtCrits(Math.ceil(potAtFull * 0.9))} at a full roster` : ''}
              {isTeamLeague ? ' · a team\'s share splits evenly across its squad' : ''}
            </Text>
            <View style={st.splitRow}>
              {splits.map((pct, i) => (
                <View key={i} style={st.splitChip}>
                  <Text style={st.splitPlace}>{i + 1}{['st', 'nd', 'rd'][i] || 'th'}</Text>
                  <Text style={st.splitPct}>{pct}%</Text>
                </View>
              ))}
            </View>
          </View>
            </>
          )}
          {critsOn && (
            <Text style={st.note}>
              Creation fee at this roster size: {fee} Crits — {isTeamLeague
                ? 'seeds the Crit pool (your team\'s entry is separate, paid when you join with your squad).'
                : `it counts as your entry (you pay the larger of entry and fee) and seeds the Crit pool.`}
              {' '}Fee ladder: 10 Crits up to 8 {isTeamLeague ? 'teams' : 'players'}, 20 up to 16, 30 above.
            </Text>
          )}
        </Section>

        {/* ══ 4 · SCHEDULE ══════════════════════════════════════════════════ */}
        <Section n="4" title="Schedule"
          hint={`Pick the exact day and time of every ${roundNoun}. Set a pattern, then adjust any row.`}>
          {/* Volume: races (free number) or meetings (round-robin) */}
          {isRaceLeague ? (
            <>
              <Text style={st.label}>{isBRFormat ? 'Number of matches' : 'Number of races'}</Text>
              <View style={st.stepperRow}>
                <TouchableOpacity style={st.stepBtn} onPress={() => setRaces(racesCount - 1)} disabled={racesCount <= RACES_MIN}>
                  <Ionicons name="remove" size={18} color={racesCount <= RACES_MIN ? '#555' : '#fff'} />
                </TouchableOpacity>
                <TextInput
                  style={st.stepInput}
                  value={racesInput}
                  onChangeText={setRacesInput}
                  onBlur={() => setRaces(racesInput)}
                  onSubmitEditing={() => setRaces(racesInput)}
                  keyboardType="numeric"
                  maxLength={3}
                />
                <TouchableOpacity style={st.stepBtn} onPress={() => setRaces(racesCount + 1)} disabled={racesCount >= RACES_MAX}>
                  <Ionicons name="add" size={18} color={racesCount >= RACES_MAX ? '#555' : '#fff'} />
                </TouchableOpacity>
                <View style={[st.pillRow, { marginLeft: 10, flex: 1 }]}>
                  {[4, 6, 8, 10, 12, 16, 20].map((n) => (
                    <Pill key={n} label={String(n)} small active={racesCount === n} onPress={() => setRaces(n)} />
                  ))}
                </View>
              </View>
              <Text style={st.note}>Any number from {RACES_MIN} to {RACES_MAX}. Standings are the total points across every {roundNoun}.</Text>
            </>
          ) : (
            <>
              <Text style={st.label}>Play each opponent</Text>
              <View style={st.pillRow}>
                {MEETINGS_OPTIONS.map((k) => (
                  <Pill key={k} label={`${k}×`} active={meetings === k} onPress={() => setMeetings(k)} />
                ))}
              </View>
              <Text style={st.note}>
                {meetings === 2 ? 'Home-and-away, like division rivals. ' : ''}
                {totalRounds} rounds at a full {maxPlayers}-{isTeamLeague ? 'team' : 'player'} roster — fewer joins at lock means fewer rounds (the tail of the calendar goes unused), never more.
              </Text>
            </>
          )}

          {/* Rhythm generator + per-round rows + summary (shared with the
              Command Center). */}
          <LeagueCalendarEditor
            roundTimes={roundTimes}
            onChange={setRoundTimes}
            pattern={pattern}
            onPattern={setPattern}
            totalRounds={totalRounds}
            roundNoun={roundNoun}
            durationMinutes={durationMin}
            editable
            seasonEnd={seasonEnd}
            gamesPerEntrant={isRaceLeague ? null : gamesPerEntrant}
            entrantNoun={isTeamLeague ? 'team' : 'entrant'}
            minLeadMs={userData?.role === 'admin' ? LEAGUE_ADMIN_MIN_LEAD_MS : undefined}
            onValidity={setScheduleOk}
          />
        </Section>

        {/* ══ 5 · POST ══════════════════════════════════════════════════════ */}
        <Section n="5" title="Post it"
          hint={critsOn ? 'Your cost, then straight into the Command Center.' : 'Straight into the Command Center.'}>
          {critsOn && (
            <View style={st.costBox}>
              <Text style={st.costTitle}>Your cost to post</Text>
              <Text style={st.costRow}>
                {isTeamLeague
                  ? `Creation fee (commissioner): ${fmtCrits(fee)}`
                  : entryNum >= fee
                    ? `Entry: ${fmtCrits(entryNum)} (covers the ${fee}-Crit creation fee)`
                    : `Creation fee (covers your entry): ${fmtCrits(fee)}`}
              </Text>
              <Text style={st.costTotal}>Total: {fmtCrits(creatorCost)} — all of it seeds the Crit pool</Text>
            </View>
          )}

          <TouchableOpacity
            style={[st.createBtn, !canCreate && st.createBtnDisabled]}
            disabled={!canCreate}
            onPress={onCreate}
          >
            {creating
              ? <ActivityIndicator color="#000" />
              : <Text style={st.createBtnText}>{critsOn ? `Spend ${creatorCost} Crits & Post League` : 'Post League'}</Text>}
          </TouchableOpacity>
          {!canCreate && !creating && (
            <Text style={[st.note, { textAlign: 'center' }]}>
              {!game ? 'Pick a game to continue.'
                : leagueName.trim().length < 3 ? 'Give the league a name (3+ characters).'
                  : entryBelowMin ? `Entry must be at least ${MIN_ENTRY} Crits.`
                    : !scheduleOk ? 'Fix the schedule above.'
                      : (game === 'other' && !communityGameKeyFromName(communityName)) ? 'Name the game.'
                        : ''}
            </Text>
          )}
          <Text style={[st.note, { textAlign: 'center', marginTop: 8 }]}>
            Next stop: your League Command Center — host mode{isRacingGame ? ', tracks, vehicle class' : ''} and every remaining dial, editable until it locks.
          </Text>
        </Section>
      </ScrollView>

      <UpgradeMembershipModal
        visible={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgraded={() => setShowUpgradeModal(false)}
        onSeeAllPlans={() => navigation.navigate('Subscription')}
      />
      <BuyCritsModal
        visible={!!critShortfall}
        shortfall={critShortfall}
        onClose={() => setCritShortfall(null)}
        onPurchased={() => setCritShortfall(null)}
        onUpgradePlan={() => navigation.navigate('Subscription')}
      />

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

const st = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#0b0b0b' },
  container: { padding: 14, paddingBottom: 120, maxWidth: 760, width: '100%', alignSelf: 'center' },
  title: { color: '#fff', fontSize: 24, fontWeight: '900', marginBottom: 4 },
  subtitle: { color: '#9aa0b4', fontSize: 13, marginBottom: 14, lineHeight: 18 },

  card: {
    backgroundColor: '#111318', borderColor: '#20242c', borderWidth: 1,
    borderRadius: 14, padding: 14, marginTop: 12,
  },
  cardHead: { flexDirection: 'row', alignItems: 'center', marginBottom: 6 },
  cardNum: {
    width: 26, height: 26, borderRadius: 13, backgroundColor: GOLD,
    alignItems: 'center', justifyContent: 'center', marginRight: 10,
  },
  cardNumText: { color: '#000', fontWeight: '900', fontSize: 13 },
  cardTitle: { color: '#fff', fontSize: 16, fontWeight: '900' },
  cardHint: { color: '#8a8a8a', fontSize: 11, marginTop: 1 },

  label: { color: '#ddd', fontSize: 13, fontWeight: '800', marginTop: 14, marginBottom: 6 },
  miniLabel: { color: '#8a8a8a', fontSize: 11, fontWeight: '800', marginTop: 10, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.6 },
  note: { color: '#8a8a8a', fontSize: 11, marginTop: 6, lineHeight: 15 },
  warn: { color: '#ff6b6b', fontSize: 12, marginTop: 6, fontWeight: '700' },
  pillRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  pill: {
    flexDirection: 'row', alignItems: 'center',
    borderWidth: 1, borderColor: '#2a2a2a', backgroundColor: '#151515',
    paddingVertical: 7, paddingHorizontal: 12, borderRadius: 10,
  },
  pillSmall: { paddingVertical: 5, paddingHorizontal: 9, borderRadius: 8 },
  pillActive: { backgroundColor: GOLD, borderColor: GOLD },
  pillDisabled: { opacity: 0.4 },
  pillText: { color: '#ddd', fontWeight: '800', fontSize: 12 },
  pillTextSmall: { fontSize: 11 },
  pillTextActive: { color: '#000' },
  input: {
    backgroundColor: '#0f1115', borderColor: '#2a2f36', borderWidth: 1,
    borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10,
    color: '#fff', fontSize: 14, marginTop: 2,
  },
  inputShort: { maxWidth: 180 },
  multiline: { minHeight: 96, textAlignVertical: 'top' },

  // Game search
  searchBox: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#0f1115', borderColor: '#2a2f36', borderWidth: 1,
    borderRadius: 10, paddingHorizontal: 12, paddingVertical: 2, marginTop: 8, marginBottom: 8,
  },
  searchInput: { flex: 1, color: '#fff', fontSize: 14, paddingVertical: 9, outlineStyle: 'none' },
  selectedRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 6 },
  selectedChip: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: GOLD,
    borderRadius: 999, paddingVertical: 6, paddingLeft: 12, paddingRight: 6,
  },
  selectedChipText: { color: '#000', fontWeight: '900', fontSize: 13 },
  selectedChipX: { marginLeft: 8, backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: 999, padding: 3 },
  verifiedTag: { color: CYAN, fontSize: 11, fontWeight: '800' },
  reportedTag: { color: '#8a8a8a', fontSize: 11, fontWeight: '800' },

  // Info boxes
  infoBox: {
    backgroundColor: '#0d0f13', borderLeftWidth: 3, borderLeftColor: GOLD,
    borderRadius: 8, padding: 10, marginTop: 10,
  },
  infoTitle: { color: GOLD, fontSize: 12, fontWeight: '900', marginBottom: 6 },
  splitRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  splitChip: {
    backgroundColor: '#151821', borderRadius: 8, paddingVertical: 4, paddingHorizontal: 10,
    alignItems: 'center', minWidth: 52,
  },
  splitPlace: { color: '#8a8a8a', fontSize: 10, fontWeight: '800' },
  splitPct: { color: '#fff', fontSize: 13, fontWeight: '900' },

  // Schedule
  stepperRow: { flexDirection: 'row', alignItems: 'center', marginTop: 8, flexWrap: 'wrap' },
  stepBtn: {
    width: 36, height: 36, borderRadius: 8, borderWidth: 1, borderColor: '#2a2f36',
    backgroundColor: '#151515', alignItems: 'center', justifyContent: 'center',
  },
  stepInput: {
    width: 64, height: 36, marginHorizontal: 6, textAlign: 'center',
    backgroundColor: '#0f1115', borderColor: '#2a2f36', borderWidth: 1, borderRadius: 8,
    color: GOLD, fontSize: 15, fontWeight: '900',
  },
  costBox: {
    backgroundColor: '#151515', borderColor: GOLD, borderWidth: 1,
    borderRadius: 12, padding: 12, marginTop: 8,
  },
  costTitle: { color: '#fff', fontSize: 13, fontWeight: '900', marginBottom: 6 },
  costRow: { color: '#cfcfcf', fontSize: 12, marginBottom: 3 },
  costTotal: { color: GOLD, fontSize: 13, fontWeight: '900', marginTop: 6 },
  createBtn: {
    backgroundColor: GOLD, borderRadius: 12, paddingVertical: 14,
    alignItems: 'center', marginTop: 16,
  },
  createBtnDisabled: { opacity: 0.4 },
  createBtnText: { color: '#000', fontSize: 15, fontWeight: '900' },
});
